import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { ScrollTrigger } from "@/lib/gsap";
import Loader from "@/components/layout/Loader";
import { LogoMark } from "@/components/Logo";

const CursorCtx = createContext(null);
const TransCtx = createContext(null);

export const useCursor = () => useContext(CursorCtx);
export const useTransition = () => useContext(TransCtx);

export function UIProvider({ children }) {
    const [variant, setVariant] = useState("default");
    const [overlay, setOverlay] = useState(null);
    const [enterDelay, setEnterDelay] = useState(0);
    const [booting, setBooting] = useState(() => {
        try {
            return !sessionStorage.getItem("zx_boot");
        } catch {
            return true;
        }
    });
    const navigate = useNavigate();
    const location = useLocation();
    const busy = useRef(false);
    const timers = useRef([]);

    const later = useCallback((fn, ms) => {
        timers.current.push(setTimeout(fn, ms));
    }, []);
    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    const finishBoot = useCallback(() => {
        try {
            sessionStorage.setItem("zx_boot", "1");
        } catch {}
        setBooting(false);
        setTimeout(() => ScrollTrigger.refresh(), 200);
    }, []);

    const go = useCallback(
        (path, label) => {
            if (busy.current || path === location.pathname) return;
            busy.current = true;
            setOverlay({ label: label || "ZENITHIX" });
            setEnterDelay(0.85);
            later(() => {
                navigate(path);
                if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
                else window.scrollTo(0, 0);
            }, 560);
            later(() => setOverlay(null), 1000);
            later(() => {
                busy.current = false;
                setEnterDelay(0);
                ScrollTrigger.refresh();
            }, 1900);
        },
        [location.pathname, navigate, later]
    );

    const bootDelay = booting ? 2.5 : 0;

    return (
        <CursorCtx.Provider value={{ variant, setVariant }}>
            <TransCtx.Provider value={{ go, enterDelay, bootDelay }}>
                {booting && <Loader onDone={finishBoot} />}
                {children}
                <AnimatePresence>
                    {overlay && (
                        <motion.div
                            key="zx-transition"
                            data-testid="page-transition-overlay"
                            className="fixed inset-0 z-[90] flex items-center justify-center bg-navy-900"
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "-100%" }}
                            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                        >
                            <div className="absolute inset-0 bg-grid opacity-30" />
                            <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-electric/20 blur-[100px]" />
                            <div className="relative flex flex-col items-center gap-6 px-6 text-center">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.7 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.12, duration: 0.35 }}
                                >
                                    <LogoMark size={56} />
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.24, duration: 0.35 }}
                                    className="font-display text-sm tracking-[0.4em] text-ice md:text-base"
                                    data-testid="transition-label"
                                >
                                    {overlay.label}
                                </motion.div>
                                <motion.div
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ delay: 0.34, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                    className="h-px w-48 origin-left bg-gradient-to-r from-transparent via-electric to-transparent"
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </TransCtx.Provider>
        </CursorCtx.Provider>
    );
}
